import { useEffect, useMemo, useRef, useState } from "react";
import ModuleSelector from "../../components/ModuleSelector.jsx";
import PlusNudge from "../../components/PlusNudge.jsx";
import PlusUpgradeModal from "../../components/PlusUpgradeModal.jsx";
import PlusUpgradePanel from "../../components/PlusUpgradePanel.jsx";
import { getPremiumVoiceOptionLabel, PREMIUM_VOICES } from "../../data/premiumVoices.js";
import { useI18n } from "../../i18n.jsx";
import { moduleJsonPath } from "../../lib/legacyPaths.js";
import { usePlus } from "../../plus/PlusProvider.jsx";
import { generatePremiumTts, isPremiumTtsConfigured, PREMIUM_TTS_MAX_CHARS } from "../../services/premiumTts.js";
import { generateVocabBurst, isVocabBurstConfigured } from "../../services/vocabBurst.js";

const MODES = [
  {
    id: "multiple-choice",
    label: "Multiple Choice",
    labelKey: "vocab.modes.multipleChoice",
    summary: "Choose the best answer.",
    summaryKey: "vocab.modes.multipleChoiceSummary"
  },
  {
    id: "writing",
    label: "Writing",
    labelKey: "vocab.modes.writing",
    summary: "Type the missing word.",
    summaryKey: "vocab.modes.writingSummary"
  },
  {
    id: "speaking-listening",
    label: "Speaking and Listening",
    labelKey: "vocab.modes.speakingListening",
    summary: "Listen, repeat, and check speech.",
    summaryKey: "vocab.modes.speakingListeningSummary"
  },
  {
    id: "vocab-burst",
    label: "Vocab Burst",
    labelKey: "vocab.modes.vocabBurst",
    summary: "Generate and speak new examples.",
    summaryKey: "vocab.modes.vocabBurstSummary"
  }
];

const DEFAULT_MODE_ID = "multiple-choice";
const LANGUAGE = "en-US";
const PREMIUM_TTS_TEST_DAILY_LIMIT = 20;
const VOCAB_BURST_LEVELS = {
  bronze: "A2",
  silver: "B1",
  gold: "B2"
};

function normalize(text) {
  return String(text || "")
    .toLowerCase()
    .replace(/[.,/#!$%^&*;:?{}=\-_`~()]/g, "")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function getRecognitionCtor() {
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
}

function getAsrErrorMessage(errorType, t) {
  if (errorType === "network") return t("asr.network");
  if (errorType === "not-allowed") return t("asr.notAllowed");
  if (errorType === "audio-capture" || errorType === "no-speech") return t("asr.audioCapture");
  if (errorType === "no-api") return t("asr.noApi");
  return t("asr.generic", { error: errorType });
}

function shuffleChoices(values) {
  return values
    .filter(Boolean)
    .map((value) => ({ value, sort: Math.random() }))
    .sort((a, b) => a.sort - b.sort)
    .map((item) => item.value);
}

function getWritingStarter(item) {
  const answer = item?.wAnswer || "";
  return answer ? answer.charAt(0) : "";
}

function uniqueWords(values) {
  const seen = new Set();
  return values
    .map((value) => String(value || "").trim())
    .filter((value) => {
      if (!value || seen.has(value.toLowerCase())) return false;
      seen.add(value.toLowerCase());
      return true;
    });
}

function getCurrentVocabWord(item) {
  return item?.cItem || item?.mItem || item?.wAnswer || item?.speaking_target || "";
}

function getVocabBurstWords(items) {
  return uniqueWords(items.flatMap((item) => [item.cItem, item.mItem, item.wAnswer, item.speaking_target])).slice(0, 80);
}

export default function VocabularyPage({ level }) {
  const { t } = useI18n();
  const plus = usePlus();
  const [moduleNumber, setModuleNumber] = useState(1);
  const [unitNumber, setUnitNumber] = useState(0);
  const [mode, setMode] = useState(DEFAULT_MODE_ID);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState(() => t("vocab.loadingData"));
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mcPromptType, setMcPromptType] = useState("context");
  const [answeredChoice, setAnsweredChoice] = useState("");
  const [feedback, setFeedback] = useState(null);
  const [writingValue, setWritingValue] = useState("");
  const [transcript, setTranscript] = useState("");
  const [isListening, setIsListening] = useState(false);
  const [asrStatus, setAsrStatus] = useState("Ready");
  const [asrError, setAsrError] = useState("");
  const [premiumBusy, setPremiumBusy] = useState(false);
  const [premiumStatus, setPremiumStatus] = useState("");
  const [premiumUpgradeOpen, setPremiumUpgradeOpen] = useState(false);
  const [premiumVoice, setPremiumVoice] = useState(PREMIUM_VOICES[0].id);
  const [stats, setStats] = useState({ correct: 0, attempts: 0 });
  const [burstWord, setBurstWord] = useState("");
  const [burstMode, setBurstMode] = useState("sentence");
  const [burstCount, setBurstCount] = useState(5);
  const [burstLength, setBurstLength] = useState("normal");
  const [burstBusy, setBurstBusy] = useState(false);
  const [burstStatus, setBurstStatus] = useState("");
  const [burstLines, setBurstLines] = useState([]);
  const [burstPremiumBusyIndex, setBurstPremiumBusyIndex] = useState(null);
  const [burstSpeakingIndex, setBurstSpeakingIndex] = useState(null);
  const [burstLineTranscript, setBurstLineTranscript] = useState("");
  const [burstLineFeedback, setBurstLineFeedback] = useState(null);

  const recognitionRef = useRef(null);
  const transcriptRef = useRef("");
  const stopTimeoutRef = useRef(null);
  const listeningRef = useRef(false);
  const finishedSpeakingRef = useRef(false);
  const soundContextRef = useRef(null);
  const premiumAudioRef = useRef(null);
  const burstListeningIndexRef = useRef(null);
  const burstTranscriptRef = useRef("");
  const burstFinishedRef = useRef(false);
  const burstTargetRef = useRef("");

  useEffect(() => {
    let ignore = false;
    setStatus(t("vocab.loadingData"));
    fetch(moduleJsonPath(level.id, moduleNumber))
      .then((response) => {
        if (!response.ok) throw new Error(t("vocab.loadModuleError", { number: moduleNumber }));
        return response.json();
      })
      .then((data) => {
        if (ignore) return;
        setItems(data);
        setStatus("");
      })
      .catch((error) => {
        if (ignore) return;
        setItems([]);
        setStatus(error.message);
      });
    return () => {
      ignore = true;
    };
  }, [level.id, moduleNumber, t]);

  const filteredItems = useMemo(() => {
    if (!unitNumber) return items;
    return items.filter((item) => Number(item.lesson) === unitNumber);
  }, [items, unitNumber]);

  useEffect(() => {
    setSelectedIndex(0);
    setStats({ correct: 0, attempts: 0 });
  }, [moduleNumber, unitNumber]);

  useEffect(() => {
    if (selectedIndex >= filteredItems.length) setSelectedIndex(0);
  }, [filteredItems.length, selectedIndex]);

  const current = filteredItems[selectedIndex] || null;
  const localizedModes = useMemo(() => MODES.map((item) => ({
    ...item,
    label: t(item.labelKey, undefined, item.label),
    summary: t(item.summaryKey, undefined, item.summary)
  })), [t]);
  const selectedMode = localizedModes.find((item) => item.id === mode) || localizedModes[0];
  const selectedPremiumVoice = PREMIUM_VOICES.find((voice) => voice.id === premiumVoice) || PREMIUM_VOICES[0];
  const vocabBurstWords = useMemo(() => getVocabBurstWords(filteredItems), [filteredItems]);
  const vocabBurstLevel = VOCAB_BURST_LEVELS[level.id] || "B1";
  const selectionText = `${t("common.module", { number: moduleNumber })} / ${unitNumber ? t("common.unit", { number: unitNumber }) : t("common.allUnits")}`;
  const progressText = filteredItems.length ? t("vocab.progress", { current: selectedIndex + 1, total: filteredItems.length }) : t("common.noItems");
  const scoreText = stats.attempts ? `${stats.correct}/${stats.attempts}` : "0/0";

  const mcQuestion = useMemo(() => {
    if (!current) return null;
    const meaningMode = mcPromptType === "meaning";
    const correct = meaningMode ? current.mItem : current.cItem;
    const choices = meaningMode
      ? [current.mItem, current.mDis1, current.mDis2, current.mDis3]
      : [current.cItem, current.cDis1, current.cDis2, current.cDis3];
    return {
      correct,
      prompt: meaningMode ? current.meaning : current.sentence,
      label: meaningMode ? t("vocab.chooseMeaning") : t("vocab.chooseBlank"),
      choices: shuffleChoices(choices)
    };
  }, [current, mcPromptType, t]);

  useEffect(() => {
    setAnsweredChoice("");
    setFeedback(null);
    setAsrError("");
    setAsrStatus("Ready");
    setIsListening(false);
    setTranscript("");
    setPremiumStatus("");
    transcriptRef.current = "";
    setWritingValue(getWritingStarter(current));
    stopRecognition();
  }, [current, mode, mcPromptType]);

  useEffect(() => {
    setBurstWord(getCurrentVocabWord(current) || vocabBurstWords[0] || "");
    setBurstLines([]);
    setBurstLineFeedback(null);
    setBurstLineTranscript("");
    setBurstStatus("");
  }, [current, moduleNumber, unitNumber, vocabBurstWords]);

  useEffect(() => {
    if (burstMode === "story" && burstLength === "collocations") {
      setBurstLength("normal");
    }
  }, [burstMode, burstLength]);

  useEffect(() => () => {
    window.clearTimeout(stopTimeoutRef.current);
    stopRecognition();
    if (premiumAudioRef.current) {
      premiumAudioRef.current.pause();
      premiumAudioRef.current = null;
    }
    if ("speechSynthesis" in window) window.speechSynthesis.cancel();
  }, []);

  function playFeedbackSound(type) {
    if (!("AudioContext" in window || "webkitAudioContext" in window)) return;
    try {
      if (window.localStorage && window.localStorage.getItem("sielpaSound") === "off") return;
    } catch {
      // Ignore unavailable localStorage.
    }
    try {
      soundContextRef.current = soundContextRef.current || new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = soundContextRef.current.createOscillator();
      const gain = soundContextRef.current.createGain();
      const now = soundContextRef.current.currentTime;
      const tones = { start: 440, success: 660, retry: 220 };
      oscillator.frequency.value = tones[type] || 440;
      gain.gain.setValueAtTime(0.0001, now);
      gain.gain.exponentialRampToValueAtTime(0.045, now + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.16);
      oscillator.connect(gain);
      gain.connect(soundContextRef.current.destination);
      oscillator.start(now);
      oscillator.stop(now + 0.17);
    } catch {
      // Feedback sound is optional.
    }
  }

  function setResultFeedback({ type, title, detail, showPopup = false }) {
    setFeedback({ type, title, detail, showPopup });
  }

  function countAttempt(isCorrect) {
    setStats((currentStats) => ({
      correct: currentStats.correct + (isCorrect ? 1 : 0),
      attempts: currentStats.attempts + 1
    }));
  }

  function goNext() {
    if (!filteredItems.length) return;
    setSelectedIndex((index) => (index + 1) % filteredItems.length);
  }

  function goPrevious() {
    if (!filteredItems.length) return;
    setSelectedIndex((index) => (index === 0 ? filteredItems.length - 1 : index - 1));
  }

  function openVocabBurst(targetWord = getCurrentVocabWord(current)) {
    if (plus.enquiryOnly) {
      setPremiumUpgradeOpen(true);
      return;
    }
    const nextWord = targetWord || vocabBurstWords[0] || "";
    setMode("vocab-burst");
    setBurstWord(nextWord);
    setBurstLines([]);
    setBurstLineFeedback(null);
    setBurstLineTranscript("");
    setBurstStatus("");
    setFeedback(null);
    stopRecognition();
  }

  function handleMcAnswer(choice) {
    if (!mcQuestion || answeredChoice) return;
    const isCorrect = normalize(choice) === normalize(mcQuestion.correct);
    setAnsweredChoice(choice);
    countAttempt(isCorrect);
    playFeedbackSound(isCorrect ? "success" : "retry");
    setResultFeedback({
      type: isCorrect ? "good" : "bad",
      title: isCorrect ? t("vocab.goodJob") : t("vocab.tryAgain"),
      detail: isCorrect ? t("vocab.niceWork") : t("vocab.correctAnswer", { answer: mcQuestion.correct }),
      showPopup: true
    });
  }

  function checkWriting() {
    if (!current) return;
    const expected = current.wAnswer || current.cItem || current.mItem;
    const typed = writingValue.trim();
    const isCorrect = normalize(typed) === normalize(expected);
    countAttempt(isCorrect);
    playFeedbackSound(isCorrect ? "success" : "retry");
    setResultFeedback({
      type: isCorrect ? "good" : "bad",
      title: isCorrect ? t("vocab.goodJob") : typed.length <= 1 ? t("vocab.typeCompleteWord") : t("vocab.tryAgain"),
      detail: isCorrect ? t("vocab.spellingMatches") : t("vocab.correctAnswer", { answer: expected }),
      showPopup: true
    });
  }

  function showWritingAnswer() {
    if (!current) return;
    const expected = current.wAnswer || current.cItem || current.mItem;
    setWritingValue(expected);
    setResultFeedback({
      type: "neutral",
      title: expected,
      detail: t("vocab.studyAnswer")
    });
  }

  function hearCurrentSentence() {
    if (!current?.speaking || !("speechSynthesis" in window)) {
      setResultFeedback({ type: "bad", title: t("vocab.audioUnavailable"), detail: t("vocab.browserSpeechUnavailable") });
      return;
    }
    const utterance = new SpeechSynthesisUtterance(current.speaking);
    utterance.lang = LANGUAGE;
    utterance.rate = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setResultFeedback({ type: "neutral", title: t("vocab.listen"), detail: t("vocab.repeatAndSpeak") });
  }

  async function hearPremiumCurrentSentence() {
    if (plus.enquiryOnly) {
      setPremiumUpgradeOpen(true);
      return;
    }
    if (!current?.speaking) return;
    if (current.speaking.length > PREMIUM_TTS_MAX_CHARS) {
      setResultFeedback({ type: "bad", title: t("vocab.premiumUnavailable"), detail: t("vocab.premiumLimit", { count: PREMIUM_TTS_MAX_CHARS }) });
      return;
    }
    if (!isPremiumTtsConfigured()) {
      setResultFeedback({ type: "bad", title: t("vocab.premiumNotConnected"), detail: t("vocab.checkSupabase") });
      return;
    }

    setPremiumBusy(true);
    setPremiumStatus(t("vocab.loadingPremium"));
    try {
      if (premiumAudioRef.current) {
        premiumAudioRef.current.pause();
        premiumAudioRef.current = null;
      }
      const result = await generatePremiumTts({
        text: current.speaking,
        language: LANGUAGE,
        voice: selectedPremiumVoice.id,
        accent: selectedPremiumVoice.accent,
        gender: selectedPremiumVoice.gender,
        voiceInstructions: selectedPremiumVoice.instructions,
        source: "vocabulary_listen",
        level: level.id,
        tool: "vocabulary",
        requestedDailyLimit: PREMIUM_TTS_TEST_DAILY_LIMIT
      });
      const audio = new Audio(result.audioUrl);
      premiumAudioRef.current = audio;
      await audio.play();
      const remainingText = typeof result.remainingToday === "number" ? t("vocab.premiumRemaining", { count: result.remainingToday }) : "";
      setPremiumStatus(`${t("vocab.premiumPlayed", { voice: selectedPremiumVoice.label })}${remainingText}`);
      setResultFeedback({ type: "neutral", title: "Premium Voice", detail: t("vocab.repeatAndSpeak") });
    } catch (error) {
      const detail = error?.code === "daily_limit"
        ? t("vocab.freePremiumUsed")
        : error?.message || t("vocab.premiumCouldNotPlay");
      setPremiumStatus(detail);
      setResultFeedback({ type: "bad", title: t("vocab.premiumUnavailable"), detail });
      if (error?.code === "daily_limit") setPremiumUpgradeOpen(true);
    } finally {
      setPremiumBusy(false);
    }
  }

  function hearGeneratedLine(line) {
    if (!line?.text || !("speechSynthesis" in window)) {
      setBurstLineFeedback({ index: line?.index ?? null, type: "bad", title: t("vocab.audioUnavailable"), detail: t("vocab.browserSpeechUnavailable") });
      return;
    }
    const utterance = new SpeechSynthesisUtterance(line.text);
    utterance.lang = LANGUAGE;
    utterance.rate = 1;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utterance);
    setBurstLineFeedback({ index: line.index, type: "neutral", title: t("vocab.listen"), detail: t("vocab.repeatGenerated") });
  }

  async function hearPremiumGeneratedLine(line) {
    if (plus.enquiryOnly) {
      setPremiumUpgradeOpen(true);
      return;
    }
    if (!line?.text) return;
    if (line.text.length > PREMIUM_TTS_MAX_CHARS) {
      setBurstLineFeedback({ index: line.index, type: "bad", title: t("vocab.premiumUnavailable"), detail: t("vocab.premiumLimit", { count: PREMIUM_TTS_MAX_CHARS }) });
      return;
    }
    if (!isPremiumTtsConfigured()) {
      setBurstLineFeedback({ index: line.index, type: "bad", title: t("vocab.premiumNotConnected"), detail: t("vocab.checkSupabase") });
      return;
    }

    setBurstPremiumBusyIndex(line.index);
    try {
      if (premiumAudioRef.current) {
        premiumAudioRef.current.pause();
        premiumAudioRef.current = null;
      }
      const result = await generatePremiumTts({
        text: line.text,
        language: LANGUAGE,
        voice: selectedPremiumVoice.id,
        accent: selectedPremiumVoice.accent,
        gender: selectedPremiumVoice.gender,
        voiceInstructions: selectedPremiumVoice.instructions,
        source: "vocab_burst_line",
        level: level.id,
        tool: "vocabulary",
        requestedDailyLimit: PREMIUM_TTS_TEST_DAILY_LIMIT
      });
      const audio = new Audio(result.audioUrl);
      premiumAudioRef.current = audio;
      await audio.play();
      setBurstLineFeedback({ index: line.index, type: "neutral", title: "Premium Voice", detail: t("vocab.repeatGenerated") });
    } catch (error) {
      const detail = error?.code === "daily_limit"
        ? t("vocab.freePremiumUsed")
        : error?.message || t("vocab.premiumCouldNotPlay");
      setBurstLineFeedback({
        index: line.index,
        type: "bad",
        title: t("vocab.premiumUnavailable"),
        detail
      });
      if (error?.code === "daily_limit") setPremiumUpgradeOpen(true);
    } finally {
      setBurstPremiumBusyIndex(null);
    }
  }

  function stopRecognition() {
    const recognition = recognitionRef.current;
    recognitionRef.current = null;
    listeningRef.current = false;
    burstListeningIndexRef.current = null;
    if (!recognition) return;
    try {
      recognition.onend = null;
      recognition.onerror = null;
      recognition.onresult = null;
      recognition.stop();
    } catch {
      // Some browsers throw after recognition has already stopped.
    }
  }

  function finishSpeakingCheck() {
    if (finishedSpeakingRef.current) return;
    finishedSpeakingRef.current = true;
    window.clearTimeout(stopTimeoutRef.current);
    stopRecognition();
    setIsListening(false);
    setAsrStatus("Ready");

    const target = current?.speaking_target || current?.cItem || current?.mItem || "";
    const spoken = transcriptRef.current.trim();
    const isCorrect = Boolean(spoken) && normalize(spoken).split(" ").includes(normalize(target));
    countAttempt(isCorrect);
    playFeedbackSound(isCorrect ? "success" : "retry");
    setResultFeedback({
      type: isCorrect ? "good" : "bad",
      title: isCorrect ? t("vocab.goodJob") : t("vocab.tryAgain"),
      detail: spoken ? t("vocab.heard", { text: spoken }) : t("vocab.noSpeech"),
      showPopup: isCorrect
    });
  }

  function startSpeakingCheck() {
    if (!current?.speaking) return;
    const RecognitionCtor = getRecognitionCtor();
    if (!RecognitionCtor) {
      setAsrError(getAsrErrorMessage("no-api", t));
      return;
    }

    let recognition;
    try {
      recognition = new RecognitionCtor();
    } catch {
      setAsrError(getAsrErrorMessage("no-api", t));
      return;
    }

    transcriptRef.current = "";
    finishedSpeakingRef.current = false;
    setTranscript("");
    setFeedback(null);
    setAsrError("");
    setAsrStatus("Listening...");
    recognition.continuous = false;
    recognition.lang = LANGUAGE;
    recognition.interimResults = true;
    recognition.onerror = (event) => {
      window.clearTimeout(stopTimeoutRef.current);
      stopRecognition();
      setIsListening(false);
      setAsrStatus("Ready");
      setAsrError(getAsrErrorMessage(event.error, t));
    };
    recognition.onresult = (event) => {
      const finalParts = [];
      const interimParts = [];
      for (let i = 0; i < event.results.length; i += 1) {
        const resultText = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalParts.push(resultText);
        } else {
          interimParts.push(resultText);
        }
      }
      const nextTranscript = finalParts.join(" ").trim() || interimParts.join(" ").trim();
      transcriptRef.current = nextTranscript;
      setTranscript(nextTranscript);
    };
    recognition.onend = () => {
      if (listeningRef.current) finishSpeakingCheck();
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      listeningRef.current = true;
      setIsListening(true);
      playFeedbackSound("start");
      stopTimeoutRef.current = window.setTimeout(finishSpeakingCheck, 5000);
    } catch (error) {
      setAsrError(getAsrErrorMessage(error.name || "start-failed", t));
      setAsrStatus("Ready");
      stopRecognition();
    }
  }

  async function handleGenerateVocabBurst() {
    if (plus.enquiryOnly) {
      setPremiumUpgradeOpen(true);
      return;
    }
    const word = burstWord.trim();
    if (!word) {
      setBurstStatus(t("vocab.chooseTarget"));
      return;
    }
    if (!isVocabBurstConfigured()) {
      setBurstStatus(t("vocab.burstNotConnected"));
      return;
    }
    if (!plus.canUseVocabBurst) {
      setBurstStatus(t("vocab.burstFreeUsed"));
      return;
    }

    setBurstBusy(true);
    setBurstStatus(t("vocab.generatingBurst"));
    setBurstLines([]);
    setBurstLineFeedback(null);
    try {
      const selectedVocab = filteredItems.find((item) => {
        const candidates = [item.cItem, item.mItem, item.wAnswer, item.speaking_target].map(normalize);
        const normalizedWord = normalize(word);
        const sourceText = normalize([item.sentence, item.meaning, item.speaking, item.writing].join(" "));
        return candidates.includes(normalizedWord) || sourceText.includes(normalizedWord);
      });
      const result = await generateVocabBurst({
        word,
        mode: burstMode,
        count: plus.isPlus ? burstCount : Math.min(burstCount, 5),
        length: burstLength,
        level: vocabBurstLevel,
        sielpaLevel: level.id,
        module: moduleNumber,
        unit: unitNumber,
        source: "react_vocabulary",
        vocab: selectedVocab
          ? {
              lesson: selectedVocab.lesson,
              sentence: selectedVocab.sentence,
              meaning: selectedVocab.meaning,
              cItem: selectedVocab.cItem,
              mItem: selectedVocab.mItem,
              wAnswer: selectedVocab.wAnswer,
              speaking: selectedVocab.speaking,
              speaking_target: selectedVocab.speaking_target
            }
          : null
      });
      if (!result.sentences.length) {
        throw new Error(t("vocab.noBurstLines"));
      }
      const quota = plus.isPlus ? null : plus.consumeVocabBurstPreview();
      setBurstLines(result.sentences);
      setBurstStatus(
        result.isDemo
          ? t("vocab.burstDemo")
          : plus.isPlus
          ? t("vocab.burstGenerated")
          : t("vocab.burstSessionUsed", { used: quota.used, limit: quota.limit })
      );
    } catch (error) {
      if (error?.code === "not_configured") {
        setBurstStatus(t("vocab.burstNotConnected"));
      } else if (error?.code === "pro_required") {
        setBurstStatus(t("vocab.burstPlusFeature"));
      } else if (error?.code === "rate_limited") {
        setBurstStatus(t("vocab.burstBusy"));
      } else if (error?.code === "network_failed") {
        setBurstStatus(t("vocab.burstNetwork"));
      } else {
        setBurstStatus(error?.message || t("vocab.burstUnavailable"));
      }
    } finally {
      setBurstBusy(false);
    }
  }

  function finishBurstLineSpeaking() {
    if (burstFinishedRef.current) return;
    burstFinishedRef.current = true;
    const lineIndex = burstListeningIndexRef.current;
    const target = burstTargetRef.current;
    window.clearTimeout(stopTimeoutRef.current);
    stopRecognition();
    setBurstSpeakingIndex(null);
    setAsrStatus("Ready");

    const spoken = burstTranscriptRef.current.trim();
    const isCorrect = Boolean(spoken) && normalize(spoken).split(" ").includes(normalize(target));
    playFeedbackSound(isCorrect ? "success" : "retry");
    setBurstLineFeedback({
      index: lineIndex,
      type: isCorrect ? "good" : "bad",
      title: isCorrect ? t("vocab.goodJob") : t("vocab.tryAgain"),
      detail: spoken ? t("vocab.heard", { text: spoken }) : t("vocab.noSpeech")
    });
    if (isCorrect) {
      setResultFeedback({
        type: "good",
        title: t("vocab.goodJob"),
        detail: t("vocab.targetInGenerated"),
        showPopup: true
      });
    }
  }

  function startBurstLineSpeaking(line, index) {
    const RecognitionCtor = getRecognitionCtor();
    if (!RecognitionCtor) {
      setBurstLineFeedback({ index, type: "bad", title: t("vocab.speechUnavailable"), detail: getAsrErrorMessage("no-api", t) });
      return;
    }

    let recognition;
    try {
      recognition = new RecognitionCtor();
    } catch {
      setBurstLineFeedback({ index, type: "bad", title: t("vocab.speechUnavailable"), detail: getAsrErrorMessage("no-api", t) });
      return;
    }

    stopRecognition();
    setIsListening(false);
    setFeedback(null);
    setBurstLineFeedback(null);
    setBurstLineTranscript("");
    burstTranscriptRef.current = "";
    burstFinishedRef.current = false;
    burstTargetRef.current = line.targetWord || burstWord;
    burstListeningIndexRef.current = index;
    recognition.continuous = false;
    recognition.lang = LANGUAGE;
    recognition.interimResults = true;
    recognition.onerror = (event) => {
      window.clearTimeout(stopTimeoutRef.current);
      stopRecognition();
      setBurstSpeakingIndex(null);
      setBurstLineFeedback({ index, type: "bad", title: t("vocab.speechError"), detail: getAsrErrorMessage(event.error, t) });
    };
    recognition.onresult = (event) => {
      const finalParts = [];
      const interimParts = [];
      for (let i = 0; i < event.results.length; i += 1) {
        const resultText = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalParts.push(resultText);
        } else {
          interimParts.push(resultText);
        }
      }
      const nextTranscript = finalParts.join(" ").trim() || interimParts.join(" ").trim();
      burstTranscriptRef.current = nextTranscript;
      setBurstLineTranscript(nextTranscript);
    };
    recognition.onend = () => {
      if (burstListeningIndexRef.current !== null) finishBurstLineSpeaking();
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
      setBurstSpeakingIndex(index);
      setAsrStatus("Listening...");
      playFeedbackSound("start");
      stopTimeoutRef.current = window.setTimeout(finishBurstLineSpeaking, 5000);
    } catch (error) {
      stopRecognition();
      setBurstLineFeedback({ index, type: "bad", title: t("vocab.speechError"), detail: getAsrErrorMessage(error.name || "start-failed", t) });
    }
  }

  useEffect(() => {
    function handleKeyDown(event) {
      const target = event.target;
      const isTyping = target instanceof HTMLInputElement
        || target instanceof HTMLTextAreaElement
        || target instanceof HTMLSelectElement
        || target?.isContentEditable;
      if (isTyping) return;

      if (mode !== "vocab-burst" && event.key === "ArrowRight") {
        event.preventDefault();
        goNext();
        return;
      }
      if (mode !== "vocab-burst" && event.key === "ArrowLeft") {
        event.preventDefault();
        goPrevious();
        return;
      }

      if (mode === "multiple-choice" && mcQuestion && !answeredChoice) {
        const keyMap = { "1": 0, "2": 1, "3": 2, "4": 3, a: 0, b: 1, c: 2, d: 3 };
        const choiceIndex = keyMap[event.key.toLowerCase()];
        if (typeof choiceIndex === "number" && mcQuestion.choices[choiceIndex]) {
          event.preventDefault();
          handleMcAnswer(mcQuestion.choices[choiceIndex]);
        }
      }

      if (mode === "speaking-listening") {
        if (event.key.toLowerCase() === "h") {
          event.preventDefault();
          hearCurrentSentence();
        } else if (event.key.toLowerCase() === "p" && !premiumBusy) {
          event.preventDefault();
          hearPremiumCurrentSentence();
        } else if (event.key.toLowerCase() === "s") {
          event.preventDefault();
          if (isListening) finishSpeakingCheck();
          else startSpeakingCheck();
        }
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [answeredChoice, isListening, mcQuestion, mode, premiumBusy, filteredItems.length]);

  function renderFeedback() {
    if (!feedback) return null;
    const canOfferBurst = mode !== "vocab-burst" && current;
    return (
      <>
        <div className={`vocabulary-feedback is-${feedback.type}`} role="status">
          <strong>{feedback.title}</strong>
          <span>{feedback.detail}</span>
        </div>
        {canOfferBurst ? (
          <div className="vocab-burst-mini-promo">
            <span>{t("vocab.wantMore")}</span>
            <button className="secondary-button" type="button" onClick={() => openVocabBurst()}>
              {t("vocab.tryVocabBurst")}
            </button>
          </div>
        ) : null}
        {feedback.showPopup ? (
          <div className="vocabulary-result-popup" role="dialog" aria-modal="true" aria-label={feedback.title}>
            <div className={`vocabulary-result-card is-${feedback.type}`}>
              <strong>{feedback.title}</strong>
              <p>{feedback.detail}</p>
              <div className="button-row">
                <button className="secondary-button" type="button" onClick={() => setFeedback((currentFeedback) => currentFeedback ? { ...currentFeedback, showPopup: false } : currentFeedback)}>
                  {t("common.close")}
                </button>
                <button className="primary-button" type="button" onClick={goNext}>
                  {t("common.next")}
                </button>
                {canOfferBurst ? (
                  <button className="secondary-button" type="button" onClick={() => openVocabBurst()}>
                    Vocab Burst
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        ) : null}
      </>
    );
  }

  function renderMultipleChoice() {
    if (!current || !mcQuestion) return null;
    return (
      <>
        <div className="vocabulary-mode-switch">
          <button className={mcPromptType === "context" ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setMcPromptType("context")}>
            {t("vocab.sentence")}
          </button>
          <button className={mcPromptType === "meaning" ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setMcPromptType("meaning")}>
            {t("vocab.definition")}
          </button>
        </div>
        <div className="vocabulary-question">
          <span>{mcQuestion.label}</span>
          <strong>{mcQuestion.prompt}</strong>
        </div>
        <div className="vocabulary-answer-grid">
          {mcQuestion.choices.map((choice) => {
            const isSelected = answeredChoice === choice;
            const isCorrect = answeredChoice && normalize(choice) === normalize(mcQuestion.correct);
            return (
              <button
                className={`vocabulary-answer${isSelected ? " is-selected" : ""}${isCorrect ? " is-correct" : ""}`}
                disabled={Boolean(answeredChoice)}
                key={choice}
                type="button"
                onClick={() => handleMcAnswer(choice)}
              >
                {choice}
              </button>
            );
          })}
        </div>
      </>
    );
  }

  function renderWriting() {
    if (!current) return null;
    return (
      <>
        <div className="vocabulary-question">
          <span>{t("vocab.completeWord")}</span>
          <strong>{current.writing || current.sentence}</strong>
        </div>
        <input
          className="vocabulary-writing-input"
          type="text"
          value={writingValue}
          onChange={(event) => setWritingValue(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") checkWriting();
          }}
          aria-label={t("vocab.writingAnswerLabel")}
        />
        <div className="button-row">
          <button className="primary-button" type="button" onClick={checkWriting}>{t("vocab.checkAnswer")}</button>
          <button className="secondary-button" type="button" onClick={showWritingAnswer}>{t("vocab.showAnswer")}</button>
        </div>
      </>
    );
  }

  function renderSpeakingListening() {
    if (!current) return null;
    return (
      <>
        <div className="vocabulary-question">
          <span>{t("vocab.targetWordLine", { word: current.speaking_target || current.cItem || current.mItem })}</span>
          <strong>{current.speaking}</strong>
        </div>
        <div className="vocabulary-speaking-toolbar">
          <div className="button-row vocabulary-speaking-buttons">
            <button className="primary-button" type="button" onClick={hearCurrentSentence}>{t("pronunciation.hear")}</button>
            <button className="secondary-button" type="button" disabled={premiumBusy} onClick={hearPremiumCurrentSentence}>
              {premiumBusy ? t("common.loading") : t("pronunciation.premiumHear")}
            </button>
            <button className="secondary-button" type="button" onClick={isListening ? finishSpeakingCheck : startSpeakingCheck}>
              {isListening ? t("vocab.stop") : t("pronunciation.speak")}
            </button>
          </div>
          <div className="vocabulary-premium-voice-control">
            <label>
              <span className="field-label">{t("common.premiumVoice")}</span>
              <select value={premiumVoice} onChange={(event) => { setPremiumVoice(event.target.value); if (plus.enquiryOnly) setPremiumUpgradeOpen(true); }}>
                {PREMIUM_VOICES.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {getPremiumVoiceOptionLabel(voice)}
                  </option>
                ))}
              </select>
            </label>
            <PlusNudge className="inline-plus-nudge" source="premium-voice-limit">
              {t("pronunciation.upgradeMoreVoice")}
            </PlusNudge>
          </div>
        </div>
        {premiumStatus ? <p className="vocabulary-premium-status">{premiumStatus}</p> : null}
        <div className="vocabulary-transcript">
          <span>{asrStatus === "Ready" ? t("common.ready") : asrStatus === "Listening..." ? t("common.listening") : asrStatus}</span>
          <p>{transcript || t("vocab.transcriptPlaceholder")}</p>
        </div>
        {asrError ? <p className="vocabulary-error">{asrError}</p> : null}
      </>
    );
  }

  function renderVocabBurstPanel() {
    const previewText = plus.enquiryOnly ? t("vocab.burstEnquiry") : plus.isPlus
      ? t("vocab.burstReadyPlus")
      : t("vocab.burstFreeLeft", { remaining: plus.vocabBurstPreviewRemaining, limit: plus.vocabBurstPreviewLimit });
    const isOutOfBurstPreviews = !plus.enquiryOnly && !plus.canUseVocabBurst;
    const isLowOnBurstPreviews = !plus.enquiryOnly && !plus.isPlus && plus.vocabBurstPreviewRemaining > 0 && plus.vocabBurstPreviewRemaining <= 1;
    const targetWordOptions = vocabBurstWords.includes(burstWord)
      ? vocabBurstWords
      : [burstWord, ...vocabBurstWords].filter(Boolean);
    return (
      <section className="vocab-burst-panel" aria-label={t("vocab.burstGeneratorLabel")}>
        <div className="vocab-burst-intro">
          <div>
            <p className="kicker">Vocab Burst</p>
            <h3>{t("vocab.burstHeadline")}</h3>
            <p>{previewText}</p>
          </div>
          <p className="vocab-burst-flow-copy">{t("vocab.burstFlow")}</p>
        </div>

        <div className="vocab-burst-controls">
          <div className="vocab-burst-target-control">
            <label>
              <span className="field-label">{t("common.targetWord")}</span>
              <select
                value={burstWord}
                onChange={(event) => setBurstWord(event.target.value)}
                disabled={!targetWordOptions.length}
              >
                {targetWordOptions.length ? null : <option value="">{t("vocab.noWords")}</option>}
                {targetWordOptions.map((word) => (
                  <option key={word} value={word}>{word}</option>
                ))}
              </select>
            </label>
            <button className="primary-button vocab-burst-generate" type="button" disabled={burstBusy || !burstWord.trim()} onClick={handleGenerateVocabBurst}>
              {burstBusy ? t("vocab.generating") : t("vocab.generate")}
            </button>
          </div>

          <div className="vocab-burst-premium-voice-control">
            <label>
              <span className="field-label">{t("common.premiumVoice")}</span>
              <select value={premiumVoice} onChange={(event) => { setPremiumVoice(event.target.value); if (plus.enquiryOnly) setPremiumUpgradeOpen(true); }}>
                {PREMIUM_VOICES.map((voice) => (
                  <option key={voice.id} value={voice.id}>
                    {getPremiumVoiceOptionLabel(voice)}
                  </option>
                ))}
              </select>
            </label>
            <PlusNudge className="inline-plus-nudge" source="vocab-burst-nudge">
              {t("vocab.unlockMore")}
            </PlusNudge>
          </div>

          <div>
            <span className="field-label">{t("vocab.mode")}</span>
            <div className="button-row">
              <button className={burstMode === "sentence" ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setBurstMode("sentence")}>
                {t("vocab.sentences")}
              </button>
              <button className={burstMode === "story" ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setBurstMode("story")}>
                {t("vocab.story")}
              </button>
            </div>
          </div>

          <div>
            <span className="field-label">{t("vocab.count")}</span>
            <div className="button-row">
              <button className={burstCount === 5 ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setBurstCount(5)}>
                5
              </button>
              <button className={burstCount === 10 ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setBurstCount(10)}>
                10
              </button>
            </div>
          </div>

          <div>
            <span className="field-label">{t("vocab.length")}</span>
            <div className="button-row">
              <button className={burstLength === "normal" ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setBurstLength("normal")}>
                {t("vocab.normal")}
              </button>
              <button className={burstLength === "short" ? "choice-button is-active" : "choice-button"} type="button" onClick={() => setBurstLength("short")}>
                {t("vocab.short")}
              </button>
              <button className={burstLength === "collocations" ? "choice-button is-active" : "choice-button"} type="button" onClick={() => {
                setBurstMode("sentence");
                setBurstLength("collocations");
              }}>
                {t("vocab.collocations")}
              </button>
            </div>
          </div>
        </div>

        {isOutOfBurstPreviews ? (
          <div className="vocab-burst-upgrade-card">
            <strong>{t("vocab.freeUsedTitle")}</strong>
            <span>{t("vocab.freeUsedCopy")}</span>
            <PlusUpgradePanel compact source="vocab-burst-limit" title={t("vocab.keepPracticingTitle")} copy={t("vocab.keepPracticingCopy")} />
          </div>
        ) : isLowOnBurstPreviews ? (
          <div className="vocab-burst-mini-promo">
            <span>{t("vocab.lastFree")}</span>
            <PlusNudge source="vocab-burst-nudge">{t("vocab.keepBurst")}</PlusNudge>
          </div>
        ) : null}

        <p className="vocab-burst-status" role="status">
          {plus.enquiryOnly ? t("vocab.burstEnquiry") : burstStatus || (isVocabBurstConfigured() ? t("vocab.ready") : t("vocab.burstNotConnected"))}
        </p>

        {burstLines.length ? (
          <section className="vocab-burst-results-section" aria-label={t("vocab.generatedPractice")}>
            <div className="vocab-burst-results-header">
              <div>
                <strong>{t("vocab.generatedPractice")}</strong>
                <span>{t("vocab.targetWordResult", { word: burstWord })}</span>
              </div>
            </div>
            <div className="vocab-burst-results">
              {burstLines.map((line, index) => {
                const nextLine = { ...line, index, targetWord: burstWord };
                const activeFeedback = burstLineFeedback && burstLineFeedback.index === index ? burstLineFeedback : null;
                return (
                  <article className="vocab-burst-line" key={line.id || `${line.text}-${index}`}>
                    <span className="vocab-burst-line-number">{index + 1}</span>
                    <div>
                      <p>{line.text}</p>
                      {line.translation ? <small>{line.translation}</small> : null}
                      <div className="button-row">
                        <button className="secondary-button" type="button" onClick={() => hearGeneratedLine(nextLine)}>{t("pronunciation.hear")}</button>
                        <button className="secondary-button" type="button" disabled={burstPremiumBusyIndex === index} onClick={() => hearPremiumGeneratedLine(nextLine)}>
                          {burstPremiumBusyIndex === index ? t("common.loading") : t("pronunciation.premiumHear")}
                        </button>
                        <button className="primary-button" type="button" onClick={() => (burstSpeakingIndex === index ? finishBurstLineSpeaking() : startBurstLineSpeaking(nextLine, index))}>
                          {burstSpeakingIndex === index ? t("vocab.stop") : t("pronunciation.speak")}
                        </button>
                      </div>
                      {burstSpeakingIndex === index ? (
                        <div className="vocabulary-transcript">
                          <span>{asrStatus === "Ready" ? t("common.ready") : asrStatus === "Listening..." ? t("common.listening") : asrStatus}</span>
                          <p>{burstLineTranscript || t("vocab.transcriptPlaceholder")}</p>
                        </div>
                      ) : null}
                      {activeFeedback ? (
                        <div className={`vocabulary-feedback is-${activeFeedback.type}`} role="status">
                          <strong>{activeFeedback.title}</strong>
                          <span>{activeFeedback.detail}</span>
                        </div>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          </section>
        ) : null}
      </section>
    );
  }

  return (
    <>
    <section className="tool-workflow vocabulary-workflow" data-sielpa-practice>
      <div className="setup-panel vocabulary-setup-card">
        <div className="vocabulary-setup-intro">
          <h2>{t("vocab.setupTitle")}</h2>
          <p>{t("vocab.setupCopy")}</p>
        </div>
        <ModuleSelector
          level={level}
          moduleNumber={moduleNumber}
          unitNumber={unitNumber}
          onModuleChange={setModuleNumber}
          onUnitChange={setUnitNumber}
        />
        <div className="vocabulary-mode-panel">
          <div className="vocabulary-mode-heading">
            <span className="field-label">{t("vocab.practiceMode")}</span>
            <h3>{t("vocab.howPractice")}</h3>
            <p>{t("vocab.opensFirst")}</p>
          </div>
          <div className="vocabulary-mode-grid">
            {localizedModes.map((item) => (
              <button className={mode === item.id ? "vocabulary-mode-card is-active" : "vocabulary-mode-card"} key={item.id} type="button" onClick={() => { setMode(item.id); if (item.id === "vocab-burst" && plus.enquiryOnly) setPremiumUpgradeOpen(true); }}>
                <strong>{item.label}</strong>
                <span>{item.summary}</span>
                {!plus.isPlus && item.id === "vocab-burst" ? <small>{plus.enquiryOnly ? t("plus.emailUs") : t("vocab.freePerDay")}</small> : null}
              </button>
            ))}
          </div>
          {!plus.isPlus && mode !== "vocab-burst" ? (
            <div className="vocab-burst-setup-promo">
              <span>{t("vocab.needFresh")}</span>
              <button className="secondary-button" type="button" onClick={() => openVocabBurst()}>
                {t("vocab.tryVocabBurst")}
              </button>
            </div>
          ) : null}
        </div>
      </div>

      <aside className="quiz-panel vocabulary-quiz-card">
        <div className="vocabulary-card-header">
          <div>
            <p className="kicker">{selectionText}</p>
            <h2>{selectedMode.label}</h2>
            <p>{selectedMode.summary}</p>
          </div>
          <div className="vocabulary-header-stats" aria-label={t("vocab.statusLabel")}>
            {mode === "vocab-burst" && plus.enquiryOnly ? <span>{t("plus.emailUs")}</span> : mode === "vocab-burst" ? (
              <>
                <span>{plus.isPlus ? "SielpA Plus" : t("vocab.freePerDayWithCount", { count: plus.vocabBurstPreviewLimit })}</span>
                <span>{plus.isPlus ? t("vocab.moreReady") : t("vocab.leftToday", { count: plus.vocabBurstPreviewRemaining })}</span>
              </>
            ) : (
              <>
                <span>{progressText}</span>
                <span>{t("common.score", { score: scoreText })}</span>
              </>
            )}
          </div>
        </div>
        {current ? (
          <div className="vocabulary-practice-body">
            {mode === "multiple-choice" ? renderMultipleChoice() : null}
            {mode === "writing" ? renderWriting() : null}
            {mode === "speaking-listening" ? renderSpeakingListening() : null}
            {mode === "vocab-burst" ? renderVocabBurstPanel() : null}
            {renderFeedback()}
            {mode !== "vocab-burst" ? (
              <div className="vocabulary-navigation">
                <button className="secondary-button" type="button" onClick={goPrevious}>{t("common.previous")}</button>
                <button className="primary-button" type="button" onClick={goNext}>{t("common.next")}</button>
              </div>
            ) : null}
          </div>
        ) : (
          <p>{status || t("vocab.noItemsAvailable")}</p>
        )}
      </aside>

    </section>
    <PlusUpgradeModal open={premiumUpgradeOpen} onClose={() => setPremiumUpgradeOpen(false)} source="vocabulary-pro-feature" />
    </>
  );
}
